import { useNavigate } from "react-router-dom";

/**
 * Navigation adapter providing v5-like API with v6 implementation
 *
 * This hook provides backward compatibility during the React Router v5 → v6 migration.
 * It wraps the v6 `useNavigate` hook and exposes both modern v6 methods and v5-compatible helpers.
 *
 * @example
 * // Modern v6 API (preferred for new code)
 * const { navigate } = useAppNavigate();
 * navigate("/path", { state: { from: "here" } });
 *
 * @example
 * // v5-compatible API (for minimal migration changes)
 * const { push, goBack, replace } = useAppNavigate();
 * push("/path");
 * goBack();
 * replace("/other-path");
 */
export function useAppNavigate() {
    const navigate = useNavigate();

    return {
        // Modern v6 API (preferred)
        navigate,

        // v5-compatible helpers for easier migration
        /**
         * Navigate to a new location (v5 history.push equivalent)
         * @param path - The path to navigate to
         * @param state - Optional state to pass with the navigation
         */
        push: (path: string, state?: Record<string, unknown>) => {
            navigate(path, { state });
        },

        /**
         * Replace current location (v5 history.replace equivalent)
         * @param path - The path to navigate to
         * @param state - Optional state to pass with the navigation
         */
        replace: (path: string, state?: Record<string, unknown>) => {
            navigate(path, { replace: true, state });
        },

        /**
         * Go back one entry in history (v5 history.goBack equivalent)
         */
        goBack: () => {
            navigate(-1);
        },

        /**
         * Go forward one entry in history (v5 history.goForward equivalent)
         */
        goForward: () => {
            navigate(1);
        },

        /**
         * Move through history by delta (v5 history.go equivalent)
         * @param delta - Number of entries to move (positive = forward, negative = back)
         */
        go: (delta: number) => {
            navigate(delta);
        },
    };
}
